import React, { useMemo, useState } from 'react'
import { usePrevious } from 'react-use'

import { ProjectInfosAPI } from '@makyoapp/makyo-sdk'
import { ErrorResponse } from '@makyoapp/makyo-sdk/api/api'
import { isValidTitle, isValidUrl } from '@makyoapp/makyo-common'
import { EmojiValue, ListSelectOption, TailwindColors } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { appConfig } from '@/configs'
import { useUpdateAppIconMutation } from '@/resolvers/project/mutation'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppContext } from '@/context/AppContext'
import { useCacheContext } from '@/context/CacheContext'
import { useSingleProjectContext } from '@/context/SingleProjectContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useReqError } from '@/hooks/useReqError'
import { MAX_APP_LOGO_FILE_SIZE } from '@/shared/constants'
import { formatBytesToSize } from '@/shared/utils'

type FileState = {
  src: string
  name: string
  file?: File
}

let tempFileName = ''

/**
 * useInfoActionAccordion is a custom hook that provides state and functions
 * for handling the functionality of an information action accordion component.
 *
 * This hook is used to manage and update the state of a project's properties
 * such as title, author, description, icon, and more.
 *
 * This hook also interacts with a number of APIs to perform actions such as
 * renaming the project, updating the project's author, description, and
 * color scheme, and more.
 *
 * It also handles the uploading of an app icon, including validation of
 * the file size and type, and providing appropriate error messages if the
 * validation fails.
 *
 * The hook returns an object of state variables and functions which can be
 * used to control the UI and functionality of the component where the hook is used.
 */
const useInfoActionAccordion = () => {
  const { project: toolbarProject, setProject: setToolbarProject } = useAppContext()

  const { project, updateProject } = useSingleProjectContext()
  const baseColor = project?.iconBgColor || TailwindColors.teal['500']
  const isUrl = project?.icon ? isValidUrl(project.icon) : false

  const { appClient } = useAppBuilderContext()

  const { imageCache } = useCacheContext()

  const [colorScheme] = useBuilderColorSchemeV2()

  const [emoji, setEmoji] = useState<undefined | string>(!isUrl ? project?.icon : undefined)
  const [file, setFile] = useState<FileState | undefined>(
    isUrl
      ? {
          src: project?.icon || '',
          name: project?.icon || '',
          file: undefined,
        }
      : undefined
  )

  const prevFileName = usePrevious(tempFileName)
  const previewName = project?.icon.startsWith('https') ? project?.icon : prevFileName

  const [loadingUpload, setLoadingUpload] = useState(false)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [uploadError, setUploadError] = useState('')

  const [titleError, setTitleError] = useState('')
  const [tempTitle, setTempTitle] = useState(project?.title || '')
  const [tempAuthor, setTempAuthor] = useState(project?.author || '')
  const [tempDesc, setTempDesc] = useState(project?.description || '')
  const printAPIError = useReqError()

  const maxFileSizeDisplay = formatBytesToSize(MAX_APP_LOGO_FILE_SIZE, 0)

  const { mutateAsync: appIconMutation } = useUpdateAppIconMutation()

  const { uploadAppIcon } = useAppBuilderContext()

  /**
   * This asynchronous function handles the selection of a new emoji.
   *
   * @param newEmoji is an object providing information about the selected emoji.
   *
   * Functionality:
   * - The state of the emoji is updated to the unified code of the new emoji.
   * - Any file that might have been selected previously is removed.
   * - If there is a project selected, the project's state is updated with the new icon and a mutation request is made to update the app icon in the backend.
   * - If there is a toolbarProject selected, the icon of the toolbarProject is also updated.
   */
  const handleSelectEmoji = async (newEmoji: EmojiValue) => {
    setEmoji(newEmoji.unified)
    setFile(undefined)
    if (project) {
      updateProject({
        ...project,
        icon: newEmoji.unified,
      })
      if (toolbarProject) {
        setToolbarProject({
          ...toolbarProject,
          icon: newEmoji.unified,
        })
      }

      uploadAppIcon(project.id, newEmoji.unified)
    }
  }

  /**
   * This asynchronous function handles the upload of an app icon.
   *
   * @param e is the event triggered when a file is uploaded.
   *
   * Functionality:
   * - The function first checks if there is a project selected, if not it returns immediately.
   * - The file uploaded by the user is extracted from the event.
   * - If no file is uploaded, the function returns immediately.
   * - A FileReader is used to read the uploaded file as a data URL.
   * - Once the file is loaded, it is stored in the state along with its name.
   * - The emoji state is reset.
   * - The size of the uploaded file is validated against a maximum size.
   * - If the file is too large, an error message is displayed and the function returns.
   * - If the file size is valid, a mutation request is made to update the app icon in the backend.
   * - The URL of the old project icon is revoked and a new URL for the uploaded file is created.
   * - The project's state is updated with the new icon URL.
   * - If there is a toolbarProject selected, its icon is also updated with the new URL.
   * - Loading status is updated throughout the process to provide user feedback.
   *
   * @todo: Proper error handling needs to be implemented.
   */
  const handleUploadAppIcon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!project) return

    const files = e.currentTarget.files
    const targetFile = files?.[0]

    if (!targetFile) return

    setLoadingUpload(true)
    setUploadError('')

    try {
      const reader = new FileReader()
      reader.readAsDataURL(targetFile)
      reader.onload = function (evt) {
        const src = evt.target?.result?.toString()
        if (src) {
          setFile({
            src,
            name: targetFile.name,
            file: targetFile,
          })
          tempFileName = targetFile.name
          setEmoji(undefined)
          //setFIcon(undefined)
        }
      }

      // Validate size
      if (targetFile.size > MAX_APP_LOGO_FILE_SIZE) {
        setLoadingUpload(false)

        return setUploadError(`Max file size allowed is ${maxFileSizeDisplay}, please upload a smaller file`)
      }

      await uploadAppIcon(project.id, targetFile)

      imageCache.update(project.id, Date.now())

      URL.revokeObjectURL(project.icon)
      const url = URL.createObjectURL(targetFile)

      // TODO: update from single project context
      updateProject({
        ...project,
        icon: url,
      })

      if (toolbarProject) {
        setToolbarProject({
          ...toolbarProject,
          icon: url,
        })
      }
      setLoadingUpload(false)
    } catch (err) {
      setLoadingUpload(false)
    }
  }

  /**
   * This asynchronous function handles the removal of an app icon.
   *
   * Functionality:
   * - The function first checks if there is a project selected, if not it returns immediately.
   * - A default emoji code is defined.
   * - The emoji state is updated with the default emoji code and any file that might have been selected previously is removed.
   * - The project's state is updated with the default emoji as the icon.
   * - If there is a toolbarProject selected, its icon is also updated with the default emoji.
   * - A mutation request is made to update the app icon in the backend with the default emoji.
   */
  const handleRemoveAppIcon = async () => {
    if (!project) return

    let defaultEmoji = '1f60d'

    setEmoji(defaultEmoji)
    setFile(undefined)

    updateProject({
      ...project,
      icon: defaultEmoji,
    })

    if (toolbarProject) {
      setToolbarProject({
        ...toolbarProject,
        icon: defaultEmoji,
      })
    }

    await uploadAppIcon(project.id, defaultEmoji || null)
  }

  /**
   * This function handles the change of the application name.
   *
   * @param e is the event triggered when the application name is changed.
   *
   * Functionality:
   * - The function first extracts the new value from the event.
   * - The temporary title state is updated with the new value.
   * - The function then checks if the new value is a valid title using the isValidTitle function.
   * - If the title is not valid, an error message is displayed.
   * - If the title is valid, any previous error message is removed.
   */
  const handleChangeAppName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value

    setTempTitle(val)

    const isValid = isValidTitle(val)

    if (!isValid) {
      setTitleError('Please enter valid name')
    } else {
      setTitleError('')
    }
  }

  /**
   * This asynchronous function handles the change of the icon background color.
   * @param color is the new color selected by the user.
   *
   * Functionality:
   * - If there is a toolbarProject selected, its color is updated with the new color.
   * - If there is a project selected, its iconBgColor is updated with the new color.
   * - A request is made to the ProjectInfosAPI to update the icon background color in the backend.
   */
  const handleChangeIconBg = async (colorHex: string) => {
    if (toolbarProject) {
      setToolbarProject({
        ...toolbarProject,
        color: colorHex,
      })
    }
    if (project) {
      updateProject({
        ...project,
        iconBgColor: colorHex,
      })
      await ProjectInfosAPI.ReqUpdateIconBGColor(project.id, colorHex)
    }
  }

  /**
   * This asynchronous function handles the selection of a color scheme.
   * @param opt is the option selected by the user.
   *
   * Functionality:
   * - If there is a project selected, its scheme is updated with the selected option's id.
   * - A request is made to the ProjectInfosAPI to update the project color scheme in the backend.
   * - If the request fails, the error is printed using the printAPIError function.
   */
  const handleSelectColorScheme = async (opt: ListSelectOption) => {
    if (project) {
      try {
        let value = opt.id
        appClient.UpdateProjectColorScheme(value)
      } catch (e) {
        printAPIError(e as ErrorResponse)
      }
    }
  }

  /**
   * This function handles updating the application name.
   *
   * Functionality:
   * - The function first checks if the temporary title is valid using the isValidTitle function.
   * - The character count of the temporary title is determined.
   * - The function checks if the character count of the temporary title is within the minimum and maximum constraints defined in the app configuration.
   * - If there is a project selected, and the temporary title is valid and within the valid length, and the temporary title is different from the current project title, then the function proceeds to update the project title.
   * - The function sets a loading state to true to provide user feedback during the update process.
   * - The project's state is updated with the new title.
   * - If there is a toolbarProject selected, its title is also updated with the new title.
   * - A request is made to the ProjectInfosAPI to rename the project in the backend.
   * - The loading state is set back to false once the update process is complete.
   *
   * The function is debounced with a delay of 500 milliseconds to prevent excessive requests when the user is typing quickly.
   */
  const handleUpdateAppName = async () => {
    const isValid = isValidTitle(tempTitle)

    const charCount = tempTitle.trim().length
    const validLength = charCount >= appConfig.charConstraints.min && charCount < appConfig.charConstraints.max

    if (project && isValid && validLength && tempTitle !== project.title) {
      await ProjectInfosAPI.ReqRename(project.id, tempTitle.trim())

      updateProject({
        ...project,
        title: tempTitle.trim(),
      })
      if (toolbarProject) {
        setToolbarProject({
          ...toolbarProject,
          title: tempTitle.trim(),
        })
      }
    }
  }

  /**
   * This asynchronous function handles updating the author of the project.
   *
   * Functionality:
   * - If there is a project selected and the temporary author is different from the project's current author, the function proceeds to update the author.
   * - A request is made to the ProjectInfosAPI to update the project's author in the backend.
   * - If the request fails, the error is printed using the printAPIError function.
   */
  const handleUpdateProjectAuthor = async () => {
    if (!tempAuthor.trim().length) return

    if (project && tempAuthor !== project.author) {
      await ProjectInfosAPI.ReqUpdateProjectAuthor(project.id, tempAuthor.trim())

      updateProject((prev) => {
        if (!prev) return
        return {
          ...prev,
          author: tempAuthor,
        }
      })
    }
  }

  /**
   * This asynchronous function handles updating the description of the project.
   *
   * Functionality:
   * - If there is a project selected and the temporary description is different from the project's current description, the function proceeds to update the description.
   * - A request is made to the ProjectInfosAPI to update the project's description in the backend.
   * - If the request fails, the error is printed using the printAPIError function.
   */
  const handleUpdateProjectDescription = async () => {
    if (!tempDesc.trim().length) return

    if (project && tempDesc !== project.description) {
      await ProjectInfosAPI.ReqUpdateDescription(project.id, tempDesc.trim())

      updateProject((prev) => {
        if (!prev) return
        return {
          ...prev,
          description: tempDesc,
        }
      })
    }
  }

  const shouldUpdateTitle = useMemo(() => {
    const charCount = tempTitle.trim().length
    const validTitleLength = charCount >= appConfig.charConstraints.min && charCount < appConfig.charConstraints.max

    return isValidTitle(tempTitle) && validTitleLength && tempTitle !== project?.title
  }, [project?.title, tempTitle])

  const shouldUpdateAuthor = useMemo(
    () => !!tempAuthor.trim().length && tempAuthor !== project?.author,
    [project?.author, tempAuthor]
  )

  const shouldUpdateDesc = useMemo(
    () => !!tempDesc.trim().length && tempDesc !== project?.description,
    [project?.description, tempDesc]
  )

  const submitButtonEnabled = shouldUpdateTitle || shouldUpdateAuthor || shouldUpdateDesc

  const handleSaveAppInfo = async () => {
    if (loadingSubmit || !project) return

    const promises = []

    if (shouldUpdateTitle) {
      promises.push(handleUpdateAppName())
    }
    if (shouldUpdateAuthor) {
      promises.push(handleUpdateProjectAuthor())
    }
    if (shouldUpdateDesc) {
      promises.push(handleUpdateProjectDescription())
    }

    if (!promises.length) return

    setLoadingSubmit(true)

    await Promise.all(promises)
      .then(() => {
        toasted('App info updated!')
      })
      .catch(printAPIError)
      .finally(() => {
        setLoadingSubmit(false)
      })

    setLoadingSubmit(false)
  }

  return {
    emoji,
    loadingUpload,
    uploadError,
    baseColor,
    project,
    isUrl,
    titleError,
    tempAuthor,
    tempDesc,
    tempTitle,
    colorScheme,
    file,
    maxFileSizeDisplay,
    setTempAuthor,
    setTempDesc,
    handleSelectEmoji,
    handleUploadAppIcon,
    handleRemoveAppIcon,
    handleChangeAppName,
    handleChangeIconBg,
    handleSelectColorScheme,
    previewName,
    handleSaveAppInfo,
    loadingSubmit,
    submitButtonEnabled,
  }
}

export default useInfoActionAccordion
